import React from 'react';
import assets from '../assets';
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSupplierSurvey } from '../hooks';
import PageLoading from '../../../components/PageLoading';
import SomethingWentWrong from '../../../components/SomethingWentWrong';
import SurveyContainer from '../../../components/Survey/Container';
import { Logo } from '../../../components/Survey/icons';
import ImageHeader from '../../../components/Survey/ImageHeader';
import { StyledContainer } from '../../../views/components/QuestionnaireV3/Question/index.styles';
import Title from '../../../components/Survey/Title';
import { toast } from 'react-toastify';
import { Box, FormControlLabel, Radio, Typography } from '@mui/material';
import { GhgSupplierSurveyAnswerInSchema } from 'src/Ghg/interfaces';
import { ErrorBuilder, SchemaErrors } from 'src/utils/validation';
import { registrationTokenParam } from '../utils';
import { Check } from '@mui/icons-material';
import themeColors from 'src/theme/colors';

type FormState = GhgSupplierSurveyAnswerInSchema;
type FormErrors = SchemaErrors<FormState>;

export const validateForm = (data: Partial<FormState>): FormErrors => {
  const builder = new ErrorBuilder(data).checkNonNull(
    ['wants_to_use_platform'],
    'validation.required'
  );

  return builder.build();
};

export default function SupplierSurveySecondStep() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const survey = useSupplierSurvey();
  const location = useLocation();

  const [searchParams, _] = useSearchParams();
  const registrationToken = searchParams.get('registration_token');

  const [form, setForm] = React.useState<FormState>({
    company_name: '',
    vat_identifier: '',
    contact_email: '',
    has_sedex_questionnaire: null,
    wants_to_use_platform: null,
    zc_code: null,
    saq_file: null,
  });

  React.useEffect(() => {
    const answer = survey.data?.answer;
    if (answer) {
      setForm({
        company_name: form.company_name || answer.company_name,
        vat_identifier: form.vat_identifier || answer.vat_identifier,
        contact_email: form.contact_email || answer.contact_email,
        has_sedex_questionnaire:
          form.has_sedex_questionnaire ?? answer.has_sedex_questionnaire,
        wants_to_use_platform:
          form.wants_to_use_platform ?? answer.wants_to_use_platform,
        zc_code: form.zc_code ?? answer.zc_code,
        saq_file: form.saq_file ?? answer.saq_file,
      });
    }
  }, [survey.data]);

  const navigateToLogin = React.useCallback(() => {
    // Clean redirect: no survey context, no `next` param.
    navigate('/login');
  }, [navigate]);

  const sendResponse = () =>
    survey.update
      .mutateAsync(form)
      .then(() => {
        if (form.wants_to_use_platform) {
          if (survey.data?.is_registered) {
            navigateToLogin();
            return;
          }

          if (!registrationToken) {
            toast.error(String(t('ghg.supplierSurvey.submitError')));
            return;
          }

          const params = new URLSearchParams({
            vat_identifier: form.vat_identifier,
            email: form.contact_email,
            organization_name: form.company_name,
            reporting_period: String(survey.data?.reporting_period || ''),
          });
          navigate(`/register_raben/${registrationToken}?${params.toString()}`);
          return;
        }

        if (form.wants_to_use_platform === false) {
          navigate(
            `../questionnaire-environment${registrationTokenParam(
              registrationToken
            )}`
          );
        }
      })
      .catch(() => {
        toast.error(String(t('ghg.supplierSurvey.submitError')));
      });
  if (survey.isError) return <SomethingWentWrong />;
  if (!survey.data) return <PageLoading />;
  if (!survey.data.answer) {
    return <Navigate to={`../initial-questions${location.search}`} replace />;
  }

  const prevLink = {
    onClick: () => {
      navigate(`../main-page${registrationTokenParam(registrationToken)}`);
    },
    label: 'ghg.supplierSurvey.back',
  };

  const nextLink = {
    label: 'ghg.supplierSurvey.next',
    onClick: () => {
      const errors = validateForm(form);
      if (Object.keys(errors).length > 0) {
        toast.error(String(t(Object.values(errors)[0].message)));
        return;
      }
      sendResponse();
    },
  };

  const showNextLink =
    form.wants_to_use_platform === false ||
    (form.wants_to_use_platform &&
      (survey.data?.is_registered === true || !!registrationToken));

  return (
    <SurveyContainer
      logo={<Logo />}
      header={
        <ImageHeader
          imageSrc={assets.questionnaire.image}
          text={t('ghg.supplierSurvey.mainHeader')}
          small
        />
      }
      prevLink={prevLink}
      nextLink={showNextLink ? nextLink : undefined}
    >
      <Title title={t('ghg.supplierSurvey.secondStep.title')} />
      <StyledContainer>
        <FormControlLabel
          label={t('ghg.supplierSurvey.secondStep.label01')}
          control={
            <Radio
              checked={form.wants_to_use_platform === true}
              onClick={() => setForm({ ...form, wants_to_use_platform: true })}
            />
          }
        />
        {form.wants_to_use_platform && survey.data?.is_registered && (
          <Box display="flex" flexDirection="row" ml="32px">
            <Check sx={{ color: themeColors.greenHover }} />
            <Typography sx={{ ml: '4px' }}>
              {t('ghg.supplierSurvey.secondStep.alreadyRegistered')}
            </Typography>
          </Box>
        )}
        <FormControlLabel
          sx={{ mt: '16px' }}
          label={t('ghg.supplierSurvey.secondStep.label02')}
          control={
            <Radio
              checked={form.wants_to_use_platform === false}
              onClick={() => setForm({ ...form, wants_to_use_platform: false })}
            />
          }
        />
      </StyledContainer>
    </SurveyContainer>
  );
}
